/**
 * chartGenerator.js — Generate chore charts, allowance plans, and resource links
 * 
 * Produces structured data that can be:
 * - Rendered in the chat UI
 * - Emailed to the user
 * - Pre-populated into Firestore on signup
 */

import { getAgeAppropriateChores, getAllowanceByAge } from './chatFlow';

// ─── Main Generator ──────────────────────────────────────────────────────────

/**
 * Generate a complete family resource package from the onboarding profile.
 * Returns: { choreCharts, allowancePlan, screenTimeSchedule, blogPosts, summary }
 */
export function generateFamilyPlan(profile) {
  const choreCharts = generateChoreCharts(profile);
  const allowancePlan = generateAllowancePlan(profile);
  const screenTimeSchedule = generateScreenTimeSchedule(profile);
  const blogPosts = curateBlogPosts(profile);
  const summary = buildSummary(profile, choreCharts, allowancePlan);

  return {
    choreCharts,
    allowancePlan,
    screenTimeSchedule,
    blogPosts,
    summary,
    generatedAt: new Date().toISOString(),
  };
}

// ─── Chore Charts ─────────────────────────────────────────────────────────────

/**
 * Build a per-kid chore chart with age-appropriate assignments.
 * Also merges any parent-specified chore types from the profile.
 */
export function generateChoreCharts(profile) {
  const kids = profile.kids || [];
  const preferredTypes = profile.chore_preferences?.types || [];
  const schedule = profile.chore_preferences?.schedule || 'evening';

  return kids.map(kid => {
    const ageChores = getAgeAppropriateChores(kid.age);
    
    // Mix age chores with parent preferences (deduplicate)
    const allChores = [...new Set([...ageChores, ...matchPreferredChores(preferredTypes, kid.age)])];
    
    // Assign chores to time slots
    const daily = allChores.slice(0, 3).map(chore => ({
      name: chore,
      frequency: 'daily',
      timeSlot: schedule,
      points: 10,
    }));
    
    const weekly = allChores.slice(3, 6).map(chore => ({
      name: chore,
      frequency: 'weekly',
      timeSlot: 'weekend',
      points: 25,
    }));

    return {
      kidName: kid.name,
      kidAge: kid.age,
      grade: kid.grade,
      dailyChores: daily,
      weeklyChores: weekly,
      totalDailyPoints: daily.reduce((s, c) => s + c.points, 0),
      totalWeeklyPoints: weekly.reduce((s, c) => s + c.points, 0),
      // Firestore-ready format
      firestoreChores: [...daily, ...weekly].map(c => ({
        id: `${kid.name.toLowerCase()}_${c.name.toLowerCase().replace(/\s+/g, '_')}`,
        title: c.name,
        assignedTo: kid.name,
        frequency: c.frequency,
        timeSlot: c.timeSlot,
        points: c.points,
        completed: false,
        createdAt: new Date(),
      })),
    };
  });
}

function matchPreferredChores(preferredTypes, age) {
  const chores = [];
  const lower = preferredTypes.map(t => t.toLowerCase().replace(/[^\w\s]/g, ''));
  
  if (lower.some(t => t.includes('bed') || t.includes('make bed'))) {
    chores.push('Make bed');
  }
  if (lower.some(t => t.includes('dish') || t.includes('kitchen'))) {
    chores.push(age && age < 8 ? 'Clear dishes from table' : 'Wash dishes');
  }
  if (lower.some(t => t.includes('sweep') || t.includes('vacuum'))) {
    chores.push(age && age < 7 ? 'Help sweep' : 'Vacuum');
  }
  if (lower.some(t => t.includes('laundry'))) {
    chores.push(age && age < 10 ? 'Put clothes in hamper' : 'Do laundry');
  }
  if (lower.some(t => t.includes('trash'))) {
    chores.push('Take out trash');
  }
  if (lower.some(t => t.includes('pet') || t.includes('animal'))) {
    chores.push('Feed pets');
  }
  if (lower.some(t => t.includes('yard') || t.includes('lawn'))) {
    chores.push(age && age < 10 ? 'Pick up yard' : 'Mow lawn');
  }
  if (lower.some(t => t.includes('homework'))) {
    chores.push('Homework (before screens)');
  }
  
  return chores;
}

// ─── Allowance Plan ───────────────────────────────────────────────────────────

export function generateAllowancePlan(profile) {
  const kids = profile.kids || [];
  const system = profile.allowance_preferences?.system || 'commission';
  const totalBudget = profile.allowance_preferences?.budget_weekly || 0;
  const savingsPct = profile.allowance_preferences?.savings_percent || 20;

  // Per-kid plans
  const kidPlans = kids.map(kid => {
    const rec = getAllowanceByAge(kid.age, system);
    
    // If budget is provided, distribute proportionally by age (older kids get more)
    let weeklyAmount = rec.weekly;
    if (totalBudget > 0 && kids.length > 0) {
      const totalAge = kids.reduce((s, k) => s + (k.age || 8), 0);
      const kidAge = kid.age || 8;
      weeklyAmount = Math.round((kidAge / totalAge) * totalBudget * 100) / 100;
    }

    return {
      kidName: kid.name,
      kidAge: kid.age,
      weeklyAmount,
      monthlyAmount: weeklyAmount * 4,
      perChoreAmount: system === 'commission' ? Math.round(weeklyAmount / 5 * 100) / 100 : 0,
      savingsGoal: Math.round(weeklyAmount * (savingsPct / 100) * 100) / 100,
      spendingAmount: Math.round(weeklyAmount * ((100 - savingsPct) / 100) * 100) / 100,
      system,
    };
  });

  return {
    system,
    totalWeeklyBudget: totalBudget || kidPlans.reduce((s, k) => s + k.weeklyAmount, 0),
    savingsPercent: savingsPct,
    kidPlans,
    tips: getAllowanceTips(system, profile),
  };
}

function getAllowanceTips(system, profile) {
  const base = [
    'Pay on the same day each week — consistency builds trust',
    'Use the "Save, Spend, Give" three-jar system',
    'Let kids make mistakes with small amounts early',
  ];
  
  if (system === 'commission') {
    base.unshift('Commission system: only pay for chores completed');
    base.push('Post a visible chore/pay chart so kids know what they earn');
  } else if (system === 'flat') {
    base.unshift('Flat allowance: money lessons separate from chore expectations');
    base.push('Still require chores — they\'re part of being a family');
  }
  
  return base;
}

// ─── Screen Time Schedule ─────────────────────────────────────────────────────

export function generateScreenTimeSchedule(profile) {
  const rules = profile.screen_time_rules || {};
  const weekday = rules.weekday_minutes || 60;
  const weekend = rules.weekend_minutes || 120;
  const rewards = rules.rewards || [];

  // Build time blocks
  const schedule = {
    weekday: {
      minutes: weekday,
      formatted: formatMinutes(weekday),
      windows: buildTimeWindows(weekday, 'weekday'),
    },
    weekend: {
      minutes: weekend,
      formatted: formatMinutes(weekend),
      windows: buildTimeWindows(weekend, 'weekend'),
    },
    rules: [
      'No screens during meals',
      'No screens 1 hour before bedtime',
      weekday === 0 ? 'No screens on school days' : `Max ${formatMinutes(weekday)} per school day`,
      ...rewards,
    ].filter(Boolean),
    choreGates: (profile.chore_preferences?.schedule || '').toLowerCase().includes('before')
      ? ['All daily chores must be complete before screen time']
      : [],
  };

  return schedule;
}

function formatMinutes(mins) {
  if (mins === 0) return 'No screen time';
  if (mins < 60) return `${mins} minutes`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} hr ${m} min` : `${h} hour${h > 1 ? 's' : ''}`;
}

function buildTimeWindows(totalMins, type) {
  if (totalMins === 0) return [];
  if (type === 'weekday') {
    // After school window
    return [{ label: 'After school / homework', start: '4:00 PM', duration: formatMinutes(totalMins) }];
  }
  // Weekend: morning + afternoon
  const half = Math.floor(totalMins / 2);
  return [
    { label: 'Morning', start: '9:00 AM', duration: formatMinutes(half) },
    { label: 'Afternoon', start: '2:00 PM', duration: formatMinutes(totalMins - half) },
  ];
}

// ─── Blog Post Curation ───────────────────────────────────────────────────────

export function curateBlogPosts(profile) {
  const painPoints = profile.challenges?.pain_points || [];

  // Map pain points to relevant blog posts on earn-echore.app/blog
  const allPosts = getBlogPostLibrary();

  // Score each post
  const scored = allPosts.map(post => {
    let score = 0;
    for (const tag of post.tags) {
      if (painPoints.some(p => p.includes(tag) || tag.includes(p))) score += 2;
    }
    // Age match
    const kidsAges = (profile.kids || []).map(k => k.age).filter(Boolean);
    if (kidsAges.some(age => age >= post.ageMin && age <= post.ageMax)) score += 1;
    return { ...post, score };
  });

  // Return top 4
  return scored.sort((a, b) => b.score - a.score).slice(0, 4);
}

function getBlogPostLibrary() {
  return [
    {
      title: 'How to Stop Nagging Your Kids About Chores (For Good)',
      slug: 'stop-nagging-kids-chores',
      description: 'Proven strategies that shift responsibility to your kids without the daily battle.',
      tags: ['nagging', 'consistency', 'independence'],
      ageMin: 5,
      ageMax: 17,
    },
    {
      title: 'The Commission Allowance System: Teaching Kids to Earn',
      slug: 'commission-allowance-system',
      description: 'Why paying per chore (not a flat allowance) teaches better money habits.',
      tags: ['allowance', 'money', 'commission'],
      ageMin: 6,
      ageMax: 17,
    },
    {
      title: 'Age-Appropriate Chores for Every Kid (Complete Guide)',
      slug: 'age-appropriate-chores-guide',
      description: 'What chores kids can actually do at each age — and how to teach them.',
      tags: ['chores', 'age', 'guide'],
      ageMin: 3,
      ageMax: 17,
    },
    {
      title: 'Screen Time Rules That Actually Work',
      slug: 'screen-time-rules-that-work',
      description: 'How to set screen time boundaries your kids will actually respect.',
      tags: ['screen_time', 'phone', 'device', 'limits'],
      ageMin: 8,
      ageMax: 17,
    },
    {
      title: 'Building a Morning Routine Kids Will Actually Follow',
      slug: 'morning-routine-kids',
      description: 'Step-by-step guide to creating a morning routine that runs on autopilot.',
      tags: ['routine', 'morning', 'consistency'],
      ageMin: 5,
      ageMax: 14,
    },
    {
      title: 'Teaching Kids to Save: The Three-Jar Money System',
      slug: 'three-jar-money-system',
      description: 'Save, Spend, Give — the classic system that teaches financial responsibility.',
      tags: ['money', 'saving', 'allowance'],
      ageMin: 5,
      ageMax: 15,
    },
    {
      title: "When Nothing Works: Resetting Your Family's Chore System",
      slug: 'reset-family-chore-system',
      description: "If you've tried everything and nothing sticks, start here.",
      tags: ['tried_everything', 'reset', 'fresh_start'],
      ageMin: 4,
      ageMax: 17,
    },
  ];
}

// ─── Summary / Email Content ──────────────────────────────────────────────────

function buildSummary(profile, choreCharts, allowancePlan) {
  const kidCount = profile.kids?.length || 0;
  const names = (profile.kids || []).map(k => k.name).join(', ');

  return {
    headline: `Your Custom Family Plan for ${names || 'Your Family'}`,
    kidCount,
    totalWeeklyChores: choreCharts.reduce((s, c) => s + c.dailyChores.length * 7 + c.weeklyChores.length, 0),
    totalWeeklyAllowance: allowancePlan.totalWeeklyBudget,
    goal30: profile.goals?.['30_day'] || '',
    goal90: profile.goals?.['90_day'] || '',
    signupCode: profile.signup_code,
  };
}

// ─── Firestore Pre-population Format ─────────────────────────────────────────

/**
 * Convert a profile + plan into Firestore-ready family document.
 * Used by the onboardUser Firebase Function.
 */
export function buildFirestoreFamily(userId, profile, plan) {
  const allChores = plan.choreCharts.flatMap(chart => chart.firestoreChores);

  return {
    userId,
    createdAt: new Date(),
    trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    subscriptionStatus: 'trial',
    
    // Kids
    kids: (profile.kids || []).map((kid, i) => ({
      id: `kid_${i}`,
      name: kid.name,
      age: kid.age,
      grade: kid.grade,
      allowance: plan.allowancePlan.kidPlans[i]?.weeklyAmount || 0,
      allowanceSystem: plan.allowancePlan.system,
    })),
    
    // Chores
    chores: allChores,
    
    // Settings
    allowanceSettings: {
      system: profile.allowance_preferences?.system || 'commission',
      payDay: 'Sunday',
      budgetWeekly: plan.allowancePlan.totalWeeklyBudget,
      savingsPercent: profile.allowance_preferences?.savings_percent || 20,
    },
    
    // Screen time
    screenTimeSettings: {
      weekdayMinutes: profile.screen_time_rules?.weekday_minutes || 60,
      weekendMinutes: profile.screen_time_rules?.weekend_minutes || 120,
      choreGated: profile.screen_time_rules?.rewards?.some(r => r.toLowerCase().includes('chore')) || false,
    },
    
    // Goals
    goals: {
      thirtyDay: profile.goals?.['30_day'] || '',
      ninetyDay: profile.goals?.['90_day'] || '',
    },
    
    // Onboarding metadata
    onboardingComplete: true,
    onboardedViaAI: true,
  };
}
